import { bulkPackingSpecKey } from '@ordo/constants/packing-plan'

/**
 * The prep recipe pages (the Oct 21 canvas, "Ordo Recipe Page — Proposed
 * Revision"): one page per dish the kitchen cooks, scaled to the day.
 *
 * - The title is the dish, its eyebrow "RECIPE 2 OF 11 · LUNCH · COMBO · 2
 *   COMPONENTS"; no yield, portion or temperature line under it.
 * - One Cook block per food on the page, "Cook · Creamy Cheddar Mac" with
 *   "Yield: 30 servings | Portion: 1½ cup" on its right, the ingredients
 *   beside the numbered steps.
 * - "Critical limits · HACCP": a row per point, its limit large on the left;
 *   then the Allergens row, "Contains Milk, Wheat." and the dietary tags. An
 *   authored allergen point ("label every container") does not print.
 * - Pack, all stops combined, in the Production Totals' columns: Pack type
 *   (Unitized or Bulk, the item under it), Deliver temp, Portion, Total, Pack,
 *   Stops.
 *
 * Combos (the canvas's combo rule): every unitized combo cooks and packs its
 * own foods on its own page (Mac + Broccoli). A food that also ships à la
 * carte packs on that combo's page, from the same batch, when it goes with
 * only one combo that day; with two or more combos its à la carte gets its own
 * page. A food with no steps (a fruit cup, a roll) is pulled, not prepped: it
 * goes on the pull & pack list (`pull-pack.ts`), never a page here, and so
 * does the stops' milk. Pure; unit-tested.
 */
import { deliverableMode } from '@/lib/chefs/deliverable-copy'
import { formatQuantity } from '@/lib/chefs/format'
import { componentTypeGroup, hotterTemperature, PACK_TYPE_ORDER } from '@/lib/chefs/pack-groups'
import { mergeDeliverablePacking } from '@/lib/chefs/packing'
import { summaryPackageLines } from '@/lib/chefs/print-packages'
import { type AggregatedRecipe, recipeKey } from '@/lib/chefs/print-plan'
import { deliverableTags } from '@/lib/chefs/print-words'
import { displayAmount, type MeasurementDisplay, type ScaleContext } from '@/lib/chefs/recipe-quantities'
import type { Component, Deliverable, Diet, Recipe, Temperature } from '@/lib/chefs/types'
import { MEALTIME_LABEL, MEALTIME_ORDER } from '@/lib/chefs/types'

import { KITCHEN_COPY, type PacketDay } from './day'
import { campusMealSections, type MealSection, mealtimeCaps, serveDayWords, type ServeWords } from './meals'
import { containerLines, deliverTemperature, portionDetail, servingSizeText } from './rows'
import type { PacketStop } from './stops'
import { mealsAcrossStops, mealTotal, stopRange, todayThenLater } from './summaries'

export { stopRange }
import type { BandSpace, ContainerLine, PacketSection, PacketUnit } from './units'

/** One place a recipe's food ships: the stop, the meal, the deliverable and the food in it. */
interface RecipeUse {
  stop: PacketStop
  section: MealSection
  deliverable: Deliverable
  component: Component
}

export function recipeSectionId(dayIso: string, key: string): string {
  return `${dayIso}|recipe|${key}`
}

/**
 * The space above a recipe page's bands: the production summary's, between
 * its groups (Matt, Oct 20: "tighten up the spacing a little bit"), no longer
 * the wide one of Abner's Oct 18 "More spacing between sections".
 */
const RECIPE_BAND_SPACE: BandSpace = 'group'

/** A HACCP point split back into its parts (`haccpPointText` on the server joins them). */
export interface HaccpParts {
  lead: string
  limit: string | null
  corrective: string | null
}

/**
 * The kitchen wire flattens each HACCP point to "Title Body Critical limit: X
 * Corrective action: Y" (`kitchen-orders.ts` `haccpPointText`); this reads the
 * labelled parts back off it, the limits for the METHOD's last lines.
 */
export function splitHaccp(text: string): HaccpParts {
  const [beforeCorrective = '', corrective] = text.split(/\s*Corrective action:\s*/)
  const [lead = '', limit] = beforeCorrective.split(/\s*Critical limit:\s*/)
  return { lead: lead.trim(), limit: limit?.trim() || null, corrective: corrective?.trim() || null }
}

/**
 * The recipe's critical limits, each once, in its order. Every one prints: a
 * METHOD line holds a limit of any length, where the strip they replace
 * dropped all past the fourth.
 */
export function criticalLimits(recipe: Pick<Recipe, 'haccp'>): string[] {
  const limits: string[] = []
  for (const point of recipe.haccp) {
    const { limit } = splitHaccp(point.text)
    if (limit && !limits.includes(limit)) {
      limits.push(limit)
    }
  }
  return limits
}

/** HACCP in short lines: what to do and the corrective action; the limit is in the METHOD. */
export function haccpLines(recipe: Pick<Recipe, 'haccp'>): string[] {
  return recipe.haccp.flatMap((point) => {
    const { lead, corrective } = splitHaccp(point.text)
    const line = [lead, corrective ? `Corrective action: ${corrective}` : null].filter(Boolean).join(' ')
    return line ? [line] : []
  })
}

/**
 * The kitchen cooks this food: it has a recipe with steps. A recipe with none (a fruit cup, a roll, a juice) is
 * pulled from stock, not prepped, and goes on the pull & pack list instead of a recipe page.
 */
export function isPrepComponent(component: Pick<Component, 'recipe'>): boolean {
  return Boolean(component.recipe && component.recipe.steps.length > 0)
}

/** A unitized combo: a bundle packed a container per serving, holding food the kitchen cooks. */
export function isUnitizedCombo(d: Deliverable): boolean {
  return d.kind === 'bundle' && deliverableMode(d) !== 'bulk' && d.components.some(isPrepComponent)
}

/** Every place a cooked food ships today, stop by stop in route order, each stop's meals today's first. */
function prepUses(day: PacketDay, milk: ReadonlySet<string>): RecipeUse[] {
  const uses: RecipeUse[] = []
  for (const stop of day.stops) {
    for (const section of todayThenLater(campusMealSections(stop.campus, day.iso))) {
      for (const deliverable of section.block.deliverables) {
        for (const component of deliverable.components) {
          const key = recipeKey(component.name, deliverable.unit)
          if (isPrepComponent(component) && !milk.has(key)) {
            uses.push({ stop, section, deliverable, component })
          }
        }
      }
    }
  }
  return uses
}

/** A bundle that holds at least one recipe is a MEAL; numbered by size across the day, biggest first. */
export function dayMeals(day: PacketDay): Map<string, number> {
  const totals = new Map<string, number>()
  for (const stop of day.stops) {
    for (const block of stop.campus.mealtimes) {
      for (const d of block.deliverables) {
        if (d.kind === 'bundle' && d.components.some((c) => c.recipe)) {
          totals.set(d.name, (totals.get(d.name) ?? 0) + d.qty)
        }
      }
    }
  }
  const order = [...totals.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  return new Map(order.map(([name], i) => [name, i + 1]))
}

function roleWord(component: Component): string {
  const group = componentTypeGroup(component)
  return group === 'mains' ? 'ENTRÉE' : group === 'sides' ? 'SIDE' : 'CONDIMENT'
}

/** The batch one Cook block makes: its uses summed as `mergeDayRecipes` sums them, the multiplier qty-weighted. */
export function batchOf(key: string, uses: readonly RecipeUse[]): AggregatedRecipe {
  const first = uses[0]!
  let totalQty = 0
  let weighted = 0
  let temperature: Temperature | null = null
  for (const use of uses) {
    totalQty += use.deliverable.qty
    weighted += use.deliverable.qty * (use.component.servingMultiplier ?? 1)
    temperature = hotterTemperature(temperature, deliverTemperature(use.deliverable))
  }
  const multiplier = totalQty > 0 ? weighted / totalQty : (first.component.servingMultiplier ?? 1)
  const component =
    multiplier === (first.component.servingMultiplier ?? 1)
      ? first.component
      : { ...first.component, servingMultiplier: multiplier }
  return { key, component, totalQty, unit: first.deliverable.unit, temperature }
}

function scaleFor(r: AggregatedRecipe, measurement: MeasurementDisplay): ScaleContext | null {
  const recipe = r.component.recipe
  return r.unit === 'x' && r.totalQty > 0 && recipe && recipe.baseYield > 0
    ? {
        servings: r.totalQty,
        baseYield: recipe.baseYield,
        servingMultiplier: r.component.servingMultiplier,
        system: 'imperial',
        mode: measurement,
      }
    : null
}

/** "1 Calzone", "1 each (Package)": a serving of this food with its note, from its recipe portion, else the shipped one. */
function eachServing(component: Component, d: Deliverable): string | null {
  return (component.portion ? servingSizeText(component.portion) : null) ?? d.servingSize ?? null
}

/** A portion as the page prints it: the serving size with its note. */
function portionWords(portion: string | null): string | null {
  return portion
}

/** "Yield: 30 servings | Portion: 1½ cup"; a recipe measured by weight says how much is needed. */
export function yieldLine(r: AggregatedRecipe, portion: string | null): string {
  const amount =
    r.unit === 'x'
      ? `${r.totalQty.toLocaleString()} ${r.totalQty === 1 ? 'serving' : 'servings'}`
      : `${formatQuantity(r.totalQty, r.unit)} needed`
  const words = portionWords(portion)
  return words ? `Yield: ${amount} | Portion: ${words}` : `Yield: ${amount}`
}

/** The first recipe-backed food of a bulk row owns its containers; the others say whose they ride in. */
function packContainers(use: RecipeUse): ContainerLine[] {
  const owner = use.deliverable.components.find((c) => c.recipe)
  if (owner && owner.id !== use.component.id && deliverableMode(use.deliverable) === 'bulk') {
    return [{ count: null, name: `With ${owner.name}`, fill: null }]
  }
  const lines = containerLines(summaryPackageLines(use.deliverable))
  return lines.length > 0 ? lines : [{ count: null, name: 'Loose', fill: null }]
}

export const SIDE_BY_SIDE_MAX_LINES = 24

/**
 * The ingredients beside the numbered steps, as one block. A long recipe
 * prints them one after the other instead, a line per unit, so neither list
 * can run off the bottom of a page.
 */
function bodyUnits(
  sid: string,
  ingredients: Array<{ amount: string | null; name: string }>,
  method: string[],
  uses: string[][] = [],
): PacketUnit[] {
  if (ingredients.length + method.length <= SIDE_BY_SIDE_MAX_LINES) {
    return [
      { id: `${sid}|body`, group: null, role: 'block', kind: 'recipe-body', ingredients, method, uses, limits: [] },
    ]
  }
  const ingredientGroup = `${sid}|ingredients`
  const methodGroup = `${sid}|method`
  return [
    {
      id: `${ingredientGroup}|band`,
      group: ingredientGroup,
      role: 'band',
      kind: 'band',
      text: 'INGREDIENTS (AP)',
      note: null,
      level: 2,
    },
    ...ingredients.map(
      (ing, i): PacketUnit => ({
        id: `${ingredientGroup}|${i}`,
        group: ingredientGroup,
        role: 'row',
        kind: 'recipe-ingredient',
        amount: ing.amount,
        name: ing.name,
      }),
    ),
    { id: `${methodGroup}|band`, group: methodGroup, role: 'band', kind: 'band', text: 'STEPS', note: null, level: 2 },
    ...method.map(
      (text, i): PacketUnit => ({
        id: `${methodGroup}|${i}`,
        group: methodGroup,
        role: 'row',
        kind: 'recipe-step',
        number: i + 1,
        text,
        uses: uses[i] ?? [],
      }),
    ),
  ]
}

/** A pack row's flags: its order group where its meal splits by group ("K-8"), then its own ("Field trip"). */
function packTags(use: RecipeUse): string[] {
  const group = use.section.block.groups && use.deliverable.groupLabel ? [use.deliverable.groupLabel] : []
  return [...group, ...deliverableTags(use.deliverable)]
}

/** How a pack row names its food: the deliverable, and an à la carte one says so. */
type PackRole = 'own' | 'a-la-carte' | 'same-batch'

function packName(d: Deliverable, role: PackRole): string {
  if (d.kind === 'bundle' || role === 'own') {
    return d.name
  }
  return role === 'same-batch' ? `${d.name}, à la carte · same batch` : `${d.name}, à la carte`
}

/**
 * Pack, all stops combined: one row per configuration (the food, its portion,
 * temperature, mode, packaging and meal), its stops in route order. A combo
 * row counts each container once, however many of its foods the page cooks.
 * Existing plans pool without repacking: a half pan allocated to one school
 * cannot travel to another.
 */
function packUnits(sid: string, uses: ReadonlyArray<{ use: RecipeUse; role: PackRole }>): PacketUnit[] {
  if (uses.length === 0) {
    return []
  }
  const seen = new Set<string>()
  const groups = new Map<string, { use: RecipeUse; role: PackRole; stops: string[] }>()
  for (const { use, role } of uses) {
    const d = use.deliverable
    // A combo's deliverable appears once per food the page cooks; it is one container.
    const once = `${use.stop.identity.number}|${use.section.key}|${d.id}`
    if (seen.has(once)) {
      continue
    }
    seen.add(once)
    const configuration = JSON.stringify({
      name: d.name,
      role,
      unit: d.unit,
      portion: d.servingSize,
      temperature: deliverTemperature(d),
      mode: deliverableMode(d),
      packaging: d.packaging,
      skuKind: d.packagingSkuKind,
      components: d.components.map((c) => [c.id, c.portion, c.servingMultiplier, c.isCondiment, c.condimentQty]),
      packing: d.packing?.map(bulkPackingSpecKey).sort(),
      incomplete: d.packingIncomplete,
      packedWith: d.packedWith,
      tags: packTags(use),
      meal: packMealWords(use.section),
    })
    const prior = groups.get(configuration)
    if (prior) {
      prior.use.deliverable = {
        ...prior.use.deliverable,
        ...mergeDeliverablePacking(prior.use.deliverable, d, d.name),
        qty: prior.use.deliverable.qty + d.qty,
      }
      prior.stops.push(use.stop.identity.number)
    } else {
      groups.set(configuration, { use: { ...use, deliverable: { ...d } }, role, stops: [use.stop.identity.number] })
    }
  }
  const group = `${sid}|pack`
  return [
    {
      id: `${group}|band`,
      group,
      role: 'band',
      kind: 'band',
      text: 'PACK',
      note: 'All stops combined',
      level: 1,
      space: RECIPE_BAND_SPACE,
    },
    { id: `${group}|head`, group, role: 'band', kind: 'recipe-pack-head' },
    ...[...groups.values()].map(
      ({ use, role, stops }, i): PacketUnit => ({
        id: `${group}|row|${i}`,
        group,
        role: 'row',
        kind: 'recipe-pack-row',
        packType: deliverableMode(use.deliverable) === 'bulk' ? 'Bulk' : 'Unitized',
        name: packName(use.deliverable, role),
        tags: packTags(use),
        meal: use.section.serve.later || use.section.serve.mixed ? packMealWords(use.section) : null,
        temperature: deliverTemperature(use.deliverable),
        // A bundle's whole serving, short and with a food listed twice added up ("2x"), as the other pages say it.
        portion: portionWords(
          use.deliverable.kind === 'bundle'
            ? (portionDetail(use.deliverable) ?? eachServing(use.component, use.deliverable))
            : eachServing(use.component, use.deliverable),
        ),
        total: use.deliverable.qty,
        containers: packContainers(use),
        stops: stopRange(stops),
      }),
    ),
  ]
}

/** The allergen line's dietary tags, spelled out (the canvas: VEGETARIAN, VEGAN). */
const DIET_WORDS: ReadonlyArray<[Diet, string]> = [
  ['vegetarian', 'VEGETARIAN'],
  ['vegan', 'VEGAN'],
  ['gluten-free', 'GLUTEN FREE'],
  ['dairy-free', 'DAIRY FREE'],
  ['nut-free', 'NUT FREE'],
  ['pork-free', 'NO PORK'],
]

/** The tags every food on the page carries: a combo is vegetarian only when each of its foods is. */
export function sharedDietWords(components: ReadonlyArray<Pick<Component, 'diets'>>): string[] {
  if (components.length === 0) {
    return []
  }
  return DIET_WORDS.filter(([diet]) => components.every((c) => c.diets.includes(diet))).map(([, word]) => word)
}

/** Every allergen on file for the page's foods, each once, in the order they are named. */
export function pageAllergens(components: ReadonlyArray<Pick<Component, 'allergens'>>): string[] {
  return [...new Set(components.flatMap((c) => c.allergens ?? []))]
}

/** An authored HACCP point about allergens: the Allergens row says the allergens, so its text does not print. */
function isAllergenPoint(lead: string): boolean {
  return /\ballergen/i.test(lead)
}

/**
 * "Critical limits · HACCP": a row per point of the page's recipes, each once, its limit large on the left; then the
 * Allergens row.
 */
function haccpUnits(sid: string, recipes: readonly Recipe[], components: readonly Component[]): PacketUnit[] {
  const group = `${sid}|haccp`
  const texts = [...new Set(recipes.flatMap((recipe) => recipe.haccp.map((point) => point.text)))]
  const limits = texts
    .map(splitHaccp)
    .filter((parts) => !isAllergenPoint(parts.lead) && (parts.lead || parts.limit))
    .map(
      (parts, i): PacketUnit => ({
        id: `${group}|limit|${i}`,
        group,
        role: 'row',
        kind: 'recipe-limit',
        limit: parts.limit,
        lead: parts.lead,
        corrective: parts.corrective,
      }),
    )
  const contains = pageAllergens(components)
  return [
    {
      id: `${group}|band`,
      group,
      role: 'band',
      kind: 'band',
      text: 'CRITICAL LIMITS · HACCP',
      note: null,
      level: 1,
      space: RECIPE_BAND_SPACE,
    },
    ...limits,
    {
      id: `${group}|allergens`,
      group,
      role: 'row',
      kind: 'recipe-allergens',
      contains: contains.length > 0 ? contains : null,
      diets: sharedDietWords(components),
      complete: components.length > 0 && components.every((c) => c.allergensComplete === true),
    },
  ]
}

/** The page's recipes' authored tips, each once, under a TIPS band; nothing when none has any. */
function tipUnits(sid: string, recipes: readonly Recipe[]): PacketUnit[] {
  const group = `${sid}|tips`
  const tips = [...new Set(recipes.flatMap((recipe) => (recipe.tips ?? []).map((tip) => tip.text.trim())))].filter(
    Boolean,
  )
  if (tips.length === 0) {
    return []
  }
  return [
    {
      id: `${group}|band`,
      group,
      role: 'band',
      kind: 'band',
      text: 'TIPS',
      note: null,
      level: 1,
      space: RECIPE_BAND_SPACE,
    },
    ...tips.map((text, i): PacketUnit => ({ id: `${group}|${i}`, group, role: 'row', kind: 'recipe-tip', text })),
  ]
}

/** One food a page cooks: its batch and the uses it is cooked for. */
interface PageCook {
  key: string
  uses: RecipeUse[]
}

/** One recipe page: a unitized combo, or a food on its own (its à la carte said when it rides 2+ combos). */
interface DishPage {
  key: string
  title: string
  combo: boolean
  aLaCarte: boolean
  cooks: PageCook[]
  pack: Array<{ use: RecipeUse; role: PackRole }>
}

/**
 * The day's recipe pages by the canvas's combo rule: a page per unitized combo, cooking its own foods; a food's other
 * uses (à la carte, bulk) ride that page when the food goes with exactly one combo, else get their own page.
 */
export function dishPages(uses: readonly RecipeUse[]): DishPage[] {
  const keyOf = (use: RecipeUse) => recipeKey(use.component.name, use.deliverable.unit)
  const comboUses = new Map<string, RecipeUse[]>()
  const combosOf = new Map<string, Set<string>>()
  const otherUses = new Map<string, RecipeUse[]>()
  for (const use of uses) {
    const key = keyOf(use)
    if (isUnitizedCombo(use.deliverable)) {
      comboUses.set(use.deliverable.name, [...(comboUses.get(use.deliverable.name) ?? []), use])
      combosOf.set(key, (combosOf.get(key) ?? new Set()).add(use.deliverable.name))
    } else {
      otherUses.set(key, [...(otherUses.get(key) ?? []), use])
    }
  }

  const pages: DishPage[] = []
  const riding = new Set<string>()
  for (const [name, combo] of comboUses) {
    const cookKeys = [...new Set(combo.map(keyOf))]
    const cooks: PageCook[] = []
    const pack: DishPage['pack'] = combo.map((use) => ({ use, role: 'own' as const }))
    for (const key of cookKeys) {
      const own = combo.filter((use) => keyOf(use) === key)
      const others = combosOf.get(key)!.size === 1 ? (otherUses.get(key) ?? []) : []
      if (others.length > 0) {
        riding.add(key)
        pack.push(...others.map((use) => ({ use, role: 'same-batch' as const })))
      }
      cooks.push({ key, uses: [...own, ...others] })
    }
    pages.push({ key: `combo|${name.toLowerCase()}`, title: name, combo: true, aLaCarte: false, cooks, pack })
  }
  for (const [key, others] of otherUses) {
    if (riding.has(key)) {
      continue
    }
    const aLaCarte = (combosOf.get(key)?.size ?? 0) >= 2
    const name = others[0]!.component.name
    pages.push({
      key,
      title: aLaCarte ? `${name} · À la carte` : name,
      combo: false,
      aLaCarte,
      cooks: [{ key, uses: others }],
      pack: others.map((use) => ({ use, role: aLaCarte ? ('a-la-carte' as const) : ('own' as const) })),
    })
  }
  return pages
}

/** "LUNCH", "BREAKFAST + LUNCH": the meals a page's food rides in, in mealtime order. */
function pageMealtimes(page: DishPage): string {
  const mealtimes = [...new Set(page.pack.map(({ use }) => use.section.mealtime))].sort(
    (a, b) => MEALTIME_ORDER.indexOf(a) - MEALTIME_ORDER.indexOf(b),
  )
  return mealtimes.map(mealtimeCaps).join(' + ')
}

/** The eyebrow after "RECIPE 2 OF 11": "LUNCH · COMBO · 2 COMPONENTS", "LUNCH · SIDE · À LA CARTE". */
function eyebrowRest(page: DishPage): string {
  const role = page.combo
    ? `COMBO · ${page.cooks.length} ${page.cooks.length === 1 ? 'COMPONENT' : 'COMPONENTS'}`
    : [roleWord(page.cooks[0]!.uses[0]!.component), page.aLaCarte ? 'À LA CARTE' : null].filter(Boolean).join(' · ')
  return [pageMealtimes(page) || null, role].filter(Boolean).join(' · ')
}

/** Where a page sorts: its first meal, then entrées and combos before sides, the biggest first. */
function pageOrder(page: DishPage): [number, number, number] {
  const mealtime = Math.min(...page.pack.map(({ use }) => MEALTIME_ORDER.indexOf(use.section.mealtime)))
  const type = page.combo ? 0 : PACK_TYPE_ORDER.indexOf(componentTypeGroup(page.cooks[0]!.uses[0]!.component))
  const servings = page.cooks.reduce((max, cook) => Math.max(max, batchOf(cook.key, cook.uses).totalQty), 0)
  return [mealtime, type, -servings]
}

function recipeSection(day: PacketDay, page: DishPage, measurement: MeasurementDisplay): PacketSection {
  const sid = recipeSectionId(day.iso, page.key)
  const units: PacketUnit[] = []
  const components: Component[] = []
  const recipes: Recipe[] = []
  page.cooks.forEach((cook, k) => {
    const batch = batchOf(cook.key, cook.uses)
    const recipe = batch.component.recipe!
    const scale = scaleFor(batch, measurement)
    const first = cook.uses[0]!
    const amountFor = (amount: { quantity: number | null; unit: string | null; quantityBasis: string | null }) =>
      scale
        ? displayAmount(amount, scale)
        : amount.quantity !== null && amount.unit !== null
          ? `${amount.quantity} ${amount.unit}`
          : null
    const cid = `${sid}|cook|${k}`
    units.push({
      id: `${cid}|head`,
      group: null,
      role: 'band',
      kind: 'recipe-cook-head',
      dish: batch.component.name,
      yieldLine: yieldLine(batch, eachServing(batch.component, first.deliverable)),
    })
    units.push(
      ...bodyUnits(
        cid,
        recipe.ingredients.map((ing) => ({ amount: amountFor(ing), name: ing.name })),
        recipe.steps.map((step) => step.text),
        // Each step's own ingredients, scaled as the list beside it ("4.5 lb Cheddar"), as the old recipe pack printed.
        recipe.steps.map((step) =>
          step.ingredientRefs.map((ref) => {
            const amount = amountFor(ref)
            return amount ? `${amount} ${ref.name}` : ref.name
          }),
        ),
      ),
    )
    components.push(batch.component)
    recipes.push(recipe)
  })
  units.push(...haccpUnits(sid, recipes, components))
  units.push(...tipUnits(sid, recipes))
  units.push(...packUnits(sid, page.pack))

  return {
    id: sid,
    kind: 'recipe',
    dayIso: day.iso,
    band: { name: 'RECIPE', counter: null, right: KITCHEN_COPY, style: 'solid' },
    // The eyebrow's count is set once the day's pages are known (`recipeSections`).
    title: { eyebrow: eyebrowRest(page), title: page.title, note: null, dateBox: day.dateBox },
    footer: { key: `${day.iso}|recipes`, label: 'Prep Recipes', counted: false },
    units,
    repeat: [],
    // Matt, Oct 20: a recipe a little too long tightens up rather than leave its HACCP alone on a page.
    preferOnePage: true,
  }
}

export interface DayRecipeSections {
  /** Hot recipes, by meal. */
  hot: PacketSection[]
  /** Chilled, ambient and prep recipes. */
  cold: PacketSection[]
  /** The meals (`MealSection.key`) whose food these pages make, for the section's cover. */
  mealKeys: Set<string>
}

/** The day's recipe pages, hot then cold + prep, each set in meal order; the stops' milk gets none. */
export function recipeSections(day: PacketDay, measurement: MeasurementDisplay): DayRecipeSections {
  // The stops' milk rows can carry a recipe like any carton; the packet lists them on the shopping and pull lists.
  const milk = new Set(
    day.campuses.flatMap((campus) => (campus.milk ?? []).map((line) => recipeKey(line.component.name, 'x'))),
  )
  const pages = dishPages(prepUses(day, milk)).sort((a, b) => {
    const [am, at, as] = pageOrder(a)
    const [bm, bt, bs] = pageOrder(b)
    return am - bm || at - bt || as - bs || a.title.localeCompare(b.title)
  })
  const hot: PacketSection[] = []
  const cold: PacketSection[] = []
  const mealKeys = new Set<string>()
  for (const page of pages) {
    const temperature = page.pack.reduce<Temperature | null>(
      (t, { use }) => hotterTemperature(t, deliverTemperature(use.deliverable)),
      null,
    )
    ;(temperature === 'hot' ? hot : cold).push(recipeSection(day, page, measurement))
    for (const { use } of page.pack) {
      mealKeys.add(use.section.key)
    }
  }
  // Every page says where it sits among the day's recipes, in print order: hot, then cold + prep.
  const total = hot.length + cold.length
  const counted = (sections: readonly PacketSection[], before: number) =>
    sections.map((section, i) => ({
      ...section,
      title: section.title && {
        ...section.title,
        eyebrow: [`RECIPE ${before + i + 1} OF ${total}`, section.title.eyebrow].filter(Boolean).join(' · '),
      },
    }))
  return { hot: counted(hot, 0), cold: counted(cold, hot.length), mealKeys }
}

/** "Serve today", "Serve Wed 10/21 · ships today, pack separately", or both days for a meal that feeds both. */
function serveNote(serve: ServeWords): string {
  if (serve.later && serve.laterDay) {
    return `Serve ${serveDayWords(serve.laterDay)} · ships today, pack separately`
  }
  if (serve.mixed && serve.laterDay) {
    return `Serve today and ${serveDayWords(serve.laterDay)} · label each container by serve day`
  }
  return 'Serve today'
}

/**
 * A meal as PACK BY STOP names it: "Lunch" for today's, and a later day's or
 * a two-day meal with the cover's serve note, "Breakfast · Serve Wed 10/21 ·
 * ships today, pack separately".
 */
function packMealWords(section: Pick<MealSection, 'mealtime' | 'serve'>): string {
  const meal = MEALTIME_LABEL[section.mealtime]
  return section.serve.later || section.serve.mixed ? `${meal} · ${serveNote(section.serve)}` : meal
}

export function recipesCoverSectionId(dayIso: string): string {
  return `${dayIso}|recipes-cover`
}

/**
 * The recipes' section cover (Matt, Oct 20: "New section cover: "Recipes",
 * date box, meals with servings"): titled "Recipes", how many follow, what
 * they are scaled to and whether the milk printed before them, the date in the
 * date stamp like every page (Hunter, Oct 20), then each meal they make with
 * its serve day and its servings, today's first. Null when the day has no
 * recipe page.
 */
export function recipesCoverSection(
  day: PacketDay,
  recipes: DayRecipeSections,
  milkPage: boolean,
): PacketSection | null {
  if (recipes.hot.length + recipes.cold.length === 0) {
    return null
  }
  const sid = recipesCoverSectionId(day.iso)
  const meals = todayThenLater(mealsAcrossStops(day)).filter((meal) => recipes.mealKeys.has(meal.key))
  const count = recipes.hot.length + recipes.cold.length
  return {
    id: sid,
    kind: 'recipes-cover',
    dayIso: day.iso,
    band: { name: 'RECIPES', counter: null, right: KITCHEN_COPY, style: 'solid' },
    title: {
      eyebrow: null,
      title: 'Recipes',
      subtitle: `${count} ${count === 1 ? 'recipe' : 'recipes'}, scaled to this day's orders.${milkPage ? ' Milk has its own page, before this section.' : ''}`,
      note: null,
      dateBox: day.dateBox,
    },
    footer: { key: `${day.iso}|recipes`, label: 'Prep Recipes', counted: false },
    units: [
      {
        id: `${sid}|cover`,
        group: null,
        role: 'block',
        kind: 'recipes-cover',
        meals: meals.map((meal) => ({
          label: MEALTIME_LABEL[meal.mealtime],
          serve: serveNote(meal.serve),
          servings: mealTotal(meal),
        })),
      },
    ],
    repeat: [],
    preferOnePage: true,
  }
}
